/**
 * JourneyQueue.js
 *
 * Leaflet-native passenger position tracker for the QueueMapMonitor.
 * Replaces the Sinatra offertraq-18 `Queue` class (queue_class.js) which
 * used Google Maps SymbolPath icons and google.maps.DirectionsService routing.
 *
 * Key differences from the Sinatra version:
 *   • L.divIcon + inline SVG instead of google.maps.SymbolPath
 *   • OSRM for route fetching (ADR-002: no Google Maps JS API)
 *   • Alert color from server (not getRandomColor())
 *   • No client animation loop — position updates arrive via ActionCable
 *   • transitionScope() replaces indoorInit() for city → property hand-off
 *   • Air routing uses great-circle interpolation (no routing API needed)
 *
 * Leaflet (L) must be loaded as a global (window.L) before using this class.
 *
 * Usage:
 *   const jq = new JourneyQueue(sessionData)
 *   await jq.addToMap(leafletMapInstance)
 *   // later:
 *   jq.updatePosition({ lat, lng, heading, speed_kmh })
 *   // scope change:
 *   jq.transitionScope('property', propertyMapInstance)
 *   // cleanup:
 *   jq.removeFromMap()
 */

import { SVG_ICONS, SVG_VIEWBOXES, ALERT_COLORS } from "./JourneyQueueSvgIcons.js";

const OSRM_BASE = "https://router.project-osrm.org/route/v1";

// OSRM profile per movement_mode
const OSRM_PROFILE = {
  driving:     "driving",
  rideshare:   "driving",
  motorcycle:  "driving",
  golf_cart:   "driving",
  transit:     "driving",
  cycling:     "cycling",
  prm_vehicle: "foot",
  walking:     "foot",
  flying:      null,   // great-circle, no OSRM
};

export class JourneyQueue {
  /**
   * @param {Object} sessionData   Serialized map session from QueueMapMonitorController
   * @param {number} sessionData.id
   * @param {string} sessionData.scope           air|city|property
   * @param {string} sessionData.movement_mode   flying|driving|...
   * @param {Object} sessionData.position        { lat, lng, heading, speed_kmh }
   * @param {string} sessionData.alert_color     green|amber|red|purple
   * @param {string} sessionData.session_status
   * @param {Object} sessionData.passenger       { name }
   * @param {Object} sessionData.offer           { name, id }
   * @param {string} [sessionData.pnr_locator]
   */
  constructor(sessionData) {
    this.id           = sessionData.id;
    this.scope        = sessionData.scope;
    this.movementMode = sessionData.movement_mode || "walking";
    this.position     = sessionData.position      || { lat: 0, lng: 0 };
    this.alertColor   = sessionData.alert_color   || "green";
    this.sessionStatus = sessionData.session_status;
    this.passenger    = sessionData.passenger     || {};
    this.offer        = sessionData.offer         || {};
    this.pnrLocator   = sessionData.pnr_locator;

    // Leaflet layer handles
    this._map         = null;
    this._marker      = null;
    this._routeLine   = null;
    this._popup       = null;
  }

  // ── Icon building ──────────────────────────────────────────────────────────

  /**
   * Build a Leaflet L.divIcon with the transport mode SVG rotated to the given heading.
   * @param {number|null} heading   Degrees 0–359
   * @param {string}      color     Hex colour string
   * @returns {L.DivIcon}
   */
  buildIcon(heading = 0, color = null) {
    const L    = window.L;
    const fill = color || ALERT_COLORS[this.alertColor] || "#10B981";
    const path = SVG_ICONS[this.movementMode] || SVG_ICONS.driving;
    const vb   = SVG_VIEWBOXES[this.movementMode] || "0 0 24 48";
    const rotation = Number.isFinite(heading) ? heading : 0;

    // SVG icon size; badge adds 10px padding so total div is size+10
    const svgSize  = this.movementMode === "flying" ? 30 : 22;
    const badgePad = 10;
    const total    = svgSize + badgePad;

    // Dark circular badge with colour border + pulse ring = always visible above
    // Leaflet anchor circleMarkers (markerPane z-index 600 > overlayPane z-400).
    const html = `
      <div style="color:${fill};position:relative;width:${total}px;height:${total}px;">
        <div class="jq-pulse-ring"></div>
        <div class="jq-marker-wrap" style="color:${fill};width:${total}px;height:${total}px;">
          <div style="
            width:${svgSize}px;height:${svgSize}px;
            transform:rotate(${rotation}deg);
            transition:transform 0.4s ease;
            flex-shrink:0;
          ">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="${vb}"
                 width="${svgSize}" height="${svgSize}">
              <path d="${path}" fill="${fill}" stroke="#fff" stroke-width="1"
                    stroke-linejoin="round" fill-rule="evenodd"/>
            </svg>
          </div>
        </div>
      </div>`;

    return L.divIcon({
      html,
      className:   "",
      iconSize:    [total, total],
      iconAnchor:  [total / 2, total / 2],
      popupAnchor: [0, -(total / 2 + 6)],
    });
  }

  // ── OSRM routing ──────────────────────────────────────────────────────────

  /**
   * Fetch a driving/walking/cycling route from OSRM.
   * Returns an array of [lat, lng] waypoints, or [] on error.
   * Air scope: returns great-circle interpolation instead.
   *
   * @param {{lat:number,lng:number}} origin
   * @param {{lat:number,lng:number}} dest
   * @returns {Promise<[number,number][]>}
   */
  async fetchRoute(origin, dest) {
    const profile = OSRM_PROFILE[this.movementMode];

    if (!profile) {
      // Air: great-circle interpolation (50 steps)
      return interpolateGreatCircle(origin, dest, 50);
    }

    try {
      const url =
        `${OSRM_BASE}/${profile}/${origin.lng},${origin.lat};${dest.lng},${dest.lat}` +
        `?overview=full&geometries=geojson`;
      const res   = await fetch(url);
      const json  = await res.json();
      const coords = json?.routes?.[0]?.geometry?.coordinates || [];
      // OSRM returns [lng, lat]; Leaflet wants [lat, lng]
      return coords.map(([lng, lat]) => [lat, lng]);
    } catch (e) {
      console.warn("[JourneyQueue] OSRM fetch failed:", e);
      return [];
    }
  }

  // ── Map lifecycle ─────────────────────────────────────────────────────────

  /**
   * Add this queue's marker (and optional route polyline) to a Leaflet map.
   * @param {L.Map} leafletMap
   * @param {{lat:number,lng:number}} [destination]   If provided, OSRM route is drawn.
   */
  async addToMap(leafletMap, destination = null) {
    const L = window.L;
    this._map = leafletMap;

    const { lat, lng, heading } = this.position;

    // Guard: Leaflet throws "Invalid LatLng object: (NaN, NaN)" when coords are
    // undefined, NaN, or non-finite (e.g. BigDecimal serialised as JS undefined,
    // or a race between ActionCable and React state on first mount).
    const safeLat = Number(lat);
    const safeLng = Number(lng);
    if (!isFinite(safeLat) || !isFinite(safeLng)) {
      console.warn("[JourneyQueue] addToMap: invalid coords – skipped", lat, lng, this.id);
      return;
    }

    const latlng = L.latLng(safeLat, safeLng);

    // Marker
    this._marker = L.marker(latlng, {
      icon:         this.buildIcon(heading),
      zIndexOffset: 1000,   // always above anchor circleMarkers (overlayPane z-400 < markerPane z-600)
    });

    this._marker.bindPopup(this._buildPopupHTML(), {
      maxWidth:  280,
      className: "jq-popup",
    });

    this._marker.addTo(leafletMap);

    // Route polyline
    if (destination) {
      const waypoints = await this.fetchRoute({ lat, lng }, destination);
      if (waypoints.length > 1) {
        const color = ALERT_COLORS[this.alertColor] || "#10B981";
        this._routeLine = L.polyline(waypoints, {
          color,
          weight:      3,
          opacity:     0.65,
          dashArray:   "8 6",
          lineJoin:    "round",
        }).addTo(leafletMap);
      }
    }
  }

  /** Remove marker and route from the map. */
  removeFromMap() {
    this._marker?.remove();
    this._routeLine?.remove();
    this._marker    = null;
    this._routeLine = null;
    this._map       = null;
  }

  // ── Real-time updates ─────────────────────────────────────────────────────

  /**
   * Update marker position and icon rotation from a QUEUE_SESSION_POSITION_UPDATE
   * ActionCable event.
   * @param {{lat:number,lng:number,heading:number,speed_kmh:number}} pos
   * @param {string} [newAlertColor]
   */
  updatePosition(pos, newAlertColor = null) {
    if (!this._marker) return;
    if (!pos) return;

    if (newAlertColor) this.alertColor = newAlertColor;
    this.position = { ...this.position, ...pos };

    // Guard: same as addToMap — ActionCable may deliver undefined/null coords
    // during initialisation races or BigDecimal serialisation edge cases.
    const safeLat = Number(pos.lat);
    const safeLng = Number(pos.lng);
    if (!isFinite(safeLat) || !isFinite(safeLng)) {
      console.warn("[JourneyQueue] updatePosition: invalid coords – skipped", pos.lat, pos.lng, this.id);
      return;
    }

    const L = window.L;
    const latlng = L.latLng(safeLat, safeLng);

    this._marker.setLatLng(latlng);
    this._marker.setIcon(this.buildIcon(pos.heading));

    // Update popup content if it's open
    if (this._marker.isPopupOpen()) {
      this._marker.setPopupContent(this._buildPopupHTML());
    }
  }

  /**
   * Handle scope transition (e.g. city → property):
   * Remove from the current map and add to the new one with updated movement_mode.
   * @param {string}  newScope         air|city|property
   * @param {L.Map}   newMap
   * @param {string}  [newMovementMode]  e.g. "walking" when transitioning from city
   */
  transitionScope(newScope, newMap, newMovementMode = null) {
    this.removeFromMap();
    this.scope = newScope;
    if (newMovementMode) this.movementMode = newMovementMode;
    this.addToMap(newMap);
  }

  // ── Popup ─────────────────────────────────────────────────────────────────

  _buildPopupHTML() {
    const color     = ALERT_COLORS[this.alertColor] || "#10B981";
    const modeEmoji = modeIcon(this.movementMode);
    const statusPill = statusBadge(this.alertColor, this.sessionStatus);
    const name      = this.passenger?.name || "Guest";
    const offerName = this.offer?.name     || "—";
    const pnr       = this.pnrLocator      || "—";
    const speed     = this.position.speed_kmh
      ? `${Number(this.position.speed_kmh).toFixed(0)} km/h`
      : "—";

    return `
      <div style="font-family:system-ui,sans-serif;min-width:200px;">
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
          <span style="font-size:16px;">${modeEmoji}</span>
          <strong style="color:${color};font-size:13px;">${escHtml(name)}</strong>
          ${statusPill}
        </div>
        <table style="font-size:11px;color:#555;width:100%;border-collapse:collapse;">
          <tr><td style="padding:2px 4px;color:#888;">Offer</td>
              <td style="padding:2px 4px;">${escHtml(offerName)}</td></tr>
          ${pnr !== "—" ? `<tr><td style="padding:2px 4px;color:#888;">PNR</td>
              <td style="padding:2px 4px;font-family:monospace;">${escHtml(pnr)}</td></tr>` : ""}
          <tr><td style="padding:2px 4px;color:#888;">Speed</td>
              <td style="padding:2px 4px;">${speed}</td></tr>
          <tr><td style="padding:2px 4px;color:#888;">Scope</td>
              <td style="padding:2px 4px;text-transform:capitalize;">${this.scope}</td></tr>
        </table>
        <div style="margin-top:8px;display:flex;gap:6px;">
          <a href="/admin/queue_monitor/${this.id}"
             style="font-size:11px;color:${color};text-decoration:none;
                    border:1px solid ${color};padding:3px 8px;border-radius:4px;">
            → Session
          </a>
        </div>
      </div>`;
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function escHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function modeIcon(mode) {
  const map = {
    flying: "✈️", driving: "🚗", transit: "🚌", cycling: "🚲",
    motorcycle: "🏍", rideshare: "🚖", golf_cart: "🛺", prm_vehicle: "♿", walking: "🚶",
  };
  return map[mode] || "📍";
}

function statusBadge(alertColor, status) {
  const color = ALERT_COLORS[alertColor] || "#10B981";
  const label = alertColor === "red" ? "⚠ Alert"
              : alertColor === "amber" ? "⏸ Paused"
              : alertColor === "purple" ? "✈ GDS"
              : "✓ Active";
  return `<span style="font-size:10px;padding:2px 6px;border-radius:10px;
                       background:${color}22;color:${color};
                       border:1px solid ${color};font-weight:600;">
    ${label}
  </span>`;
}

/**
 * Great-circle interpolation between two lat/lng points.
 * Returns `steps` intermediate [lat, lng] pairs.
 * Used for air/flying scope where OSRM routing is not appropriate.
 */
function interpolateGreatCircle(origin, dest, steps = 50) {
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;

  const φ1 = toRad(origin.lat), λ1 = toRad(origin.lng);
  const φ2 = toRad(dest.lat),   λ2 = toRad(dest.lng);
  const d   = 2 * Math.asin(Math.sqrt(
    Math.pow(Math.sin((φ2 - φ1) / 2), 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.pow(Math.sin((λ2 - λ1) / 2), 2)
  ));

  if (d === 0) return [[origin.lat, origin.lng]];

  const result = [];
  for (let i = 0; i <= steps; i++) {
    const f = i / steps;
    const A = Math.sin((1 - f) * d) / Math.sin(d);
    const B = Math.sin(f * d) / Math.sin(d);
    const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
    const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
    const z = A * Math.sin(φ1) + B * Math.sin(φ2);
    const lat = toDeg(Math.atan2(z, Math.sqrt(x * x + y * y)));
    const lng = toDeg(Math.atan2(y, x));
    result.push([lat, lng]);
  }
  return result;
}
